import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Bell,
  CheckCheck,
  Download,
  Flag,
  Loader2,
  Play,
  Search,
  UserPlus,
} from "lucide-react";
import { PageHeader } from "@packages/ui";
import type { AuditEventRecord, PersonalAuditListQuery } from "@packages/global";
import { Pagination } from '@packages/ui';
import { accountActivityApi } from "../../services/user";
import { toNotification } from "../../utils/auditNotifications";

const PAGE_SIZE = 20;

type FilterKey = "all" | "query" | "invites" | "parse" | "runExport" | "system" | "attention";

const filterOptions: Array<{ value: FilterKey; label: string }> = [
  { value: "all", label: "全部" },
  { value: "query", label: "质疑" },
  { value: "invites", label: "协作邀请" },
  { value: "parse", label: "解析告警" },
  { value: "runExport", label: "运行/导出" },
  { value: "system", label: "系统" },
  { value: "attention", label: "待处理" },
];

function buildQuery(filter: FilterKey, page: number, keyword: string): PersonalAuditListQuery {
  const query: PersonalAuditListQuery = {
    page,
    pageSize: PAGE_SIZE,
    keyword: keyword || undefined,
  };
  if (filter === "attention") {
    query.attentionOnly = true;
  }
  if (filter === "query") {
    query.notificationCategory = "query";
  }
  if (filter === "invites") {
    query.notificationCategory = "invites";
  }
  if (filter === "parse") {
    query.notificationCategory = "parse";
  }
  if (filter === "runExport") {
    query.notificationCategory = "runExport";
  }
  if (filter === "system") {
    query.notificationCategory = "system";
  }
  return query;
}

function groupNotificationTime(time: string): string {
  const date = new Date(time);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "今天";
  if (date.toDateString() === yesterday.toDateString()) return "昨天";
  return "更早";
}

function notificationVisual(badge: string) {
  if (badge === "质疑") {
    return { Icon: Flag, box: "bg-warning-soft text-warning", action: "去答复" };
  }
  if (badge === "协作") {
    return { Icon: UserPlus, box: "bg-primary-soft text-primary-ink", action: "接受" };
  }
  if (badge === "运行") {
    return { Icon: Play, box: "bg-[#E2ECF7] text-[#2563A8]", action: "查看结果" };
  }
  if (badge === "导出") {
    return { Icon: Download, box: "bg-success-soft text-success", action: "下载" };
  }
  if (badge === "失败" || badge === "需关注") {
    return { Icon: AlertTriangle, box: "bg-danger-soft text-danger", action: "查看" };
  }
  return { Icon: Bell, box: "bg-bg-elevated text-text-secondary", action: "查看" };
}

function relativeTime(value: string): string {
  const date = new Date(value);
  const diff = Date.now() - date.getTime();
  if (Number.isNaN(diff)) return "";
  const minutes = Math.max(1, Math.floor(diff / 60000));
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  return date.toLocaleDateString();
}

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterKey>("all");
  const [keywordInput, setKeywordInput] = useState("");
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<AuditEventRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [readIds, setReadIds] = useState<Set<string>>(new Set());

  const query = useMemo(
    () => buildQuery(filter, page, keyword),
    [filter, keyword, page],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await accountActivityApi.notifications(query);
      setRows(data.items);
      setTotal(data.total);
      setReadIds(new Set(data.readEventIds));
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? "load failed");
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    void load();
  }, [load]);

  const notifications = useMemo(() => rows.map(toNotification), [rows]);
  const unreadCount = notifications.filter((item) => !readIds.has(item.id)).length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const markOneRead = async (id: string) => {
    const next = new Set(readIds);
    next.add(id);
    setReadIds(next);
    try {
      await accountActivityApi.markNotificationRead(id);
    } catch {
      const rollback = new Set(next);
      rollback.delete(id);
      setReadIds(rollback);
    }
  };

  const markCurrentPageRead = async () => {
    const ids = notifications.map((item) => item.id);
    const next = new Set(readIds);
    ids.forEach((id) => next.add(id));
    setReadIds(next);
    try {
      await accountActivityApi.markNotificationsRead(ids);
    } catch {
      setReadIds(new Set(readIds));
    }
  };

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        eyebrow="My Workspace"
        title="通知中心"
        description={`系统推送给你的消息、协作提醒与待办 · 本页 ${unreadCount} 条未读`}
        actionsSlot={
          <>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                setKeyword(keywordInput.trim());
                setPage(1);
              }}
              className="flex min-h-[38px] w-[260px] items-center gap-2 rounded-xl bg-bg-elevated px-3"
            >
              <Search className="h-[15px] w-[15px] text-text-muted" />
              <input
                value={keywordInput}
                onChange={(event) => setKeywordInput(event.target.value)}
                placeholder="搜索通知..."
                aria-label="搜索通知"
                className="min-w-0 flex-1 bg-transparent text-[12.5px] text-text-primary outline-none placeholder:text-text-muted"
              />
              {keywordInput && (
                <button
                  type="button"
                  onClick={() => {
                    setKeywordInput("");
                    setKeyword("");
                    setPage(1);
                  }}
                  className="text-[11.5px] font-medium text-text-muted hover:text-primary-ink"
                >
                  清空
                </button>
              )}
            </form>
            <button
              type="button"
              disabled={notifications.length === 0}
              onClick={() => void markCurrentPageRead()}
              className="inline-flex min-h-[38px] items-center gap-1.5 rounded-[8px] border border-border-default bg-bg-surface px-[13px] text-[12.5px] font-medium text-text-primary transition-colors hover:bg-bg-elevated disabled:cursor-not-allowed disabled:opacity-50"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              全部已读
            </button>
          </>
        }
      />

      <div className="-mx-4 bg-bg-page px-4 sm:-mx-6 sm:px-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            {filterOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setFilter(option.value);
                  setPage(1);
                }}
                className={`inline-flex items-center rounded-full border px-[13px] py-[6px] text-[12px] transition-colors ${
                  filter === option.value
                    ? "border-action-primary bg-action-primary font-semibold text-white"
                    : "border-border-default bg-bg-surface font-medium text-text-secondary hover:border-action-primary-border hover:bg-bg-elevated"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {error && (
            <div className="rounded-xl border border-danger-border bg-danger-soft px-4 py-3 text-[13px] text-danger">
              加载通知失败：{error}
            </div>
          )}

          <section>
            {loading ? (
              <div className="rounded-xl border border-border-subtle bg-bg-surface py-16 text-center text-[13px] text-text-muted">
                <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin opacity-50" />
                加载通知中...
              </div>
            ) : notifications.length === 0 ? (
              <div className="rounded-xl border border-border-subtle bg-bg-surface py-16 text-center">
                <Bell className="mx-auto h-8 w-8 text-text-muted" />
                <p className="mt-3 text-[14px] font-semibold text-text-primary">暂无通知</p>
                <p className="mt-1 text-[12.5px] text-text-muted">
                  当前筛选条件下没有需要感知或处理的消息。
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {["今天", "昨天", "更早"].map((group) => {
                  const groupItems = notifications.filter((item) => groupNotificationTime(item.time) === group);
                  if (groupItems.length === 0) return null;
                  return (
                    <section key={group} className="space-y-2">
                      <div className="font-mono text-[10.5px] text-text-muted">{group}</div>
                      <div className="overflow-hidden rounded-xl border border-border-subtle bg-bg-surface">
                        {groupItems.map((item, index) => {
                          const isRead = readIds.has(item.id);
                          const visual = notificationVisual(item.badge);
                          const Icon = visual.Icon;
                          return (
                            <article
                              key={item.id}
                              className={`flex gap-3 bg-bg-surface p-[14px_16px] transition-colors hover:bg-bg-elevated ${
                                index > 0 ? "border-t border-border-subtle" : ""
                              }`}
                            >
                              <div className={`flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[8px] ${visual.box}`}>
                                <Icon className="h-[17px] w-[17px]" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-start gap-3">
                                  <h3 className="min-w-0 flex-1 truncate text-[13px] font-semibold text-text-primary">
                                    {item.title}
                                  </h3>
                                  {!isRead && <span className="h-1.5 w-1.5 rounded-full bg-action-primary" />}
                                  <span className="shrink-0 font-mono text-[10px] text-text-muted">
                                    {relativeTime(item.time)}
                                  </span>
                                </div>
                                <p className="mt-1 text-[12px] leading-[17px] text-text-secondary">{item.description}</p>
                              </div>
                              <div className="flex shrink-0 flex-wrap items-start gap-2">
                                {!isRead && (
                                  <button
                                    type="button"
                                    onClick={() => void markOneRead(item.id)}
                                    className="inline-flex min-h-8 items-center gap-1.5 rounded-[8px] border border-border-default bg-bg-surface px-3 text-[12px] font-medium text-text-secondary transition-colors hover:bg-bg-elevated"
                                  >
                                    <CheckCheck className="h-3.5 w-3.5" />
                                    已读
                                  </button>
                                )}
                                {item.targetPath && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      void markOneRead(item.id);
                                      navigate(item.targetPath!);
                                    }}
                                    className="inline-flex min-h-8 items-center rounded-[8px] bg-action-primary px-3 text-[12px] font-semibold text-white hover:bg-action-primary-hover"
                                  >
                                    {visual.action}
                                  </button>
                                )}
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    </section>
                  );
                })}
              </div>
            )}

            {notifications.length > 0 && (
              <div className="mt-4 overflow-hidden rounded-xl border border-border-subtle bg-bg-surface">
                <Pagination
                  page={page}
                  limit={PAGE_SIZE}
                  total={total}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
